// ClientService.tsx
import { BaseService } from '../../core/BaseService';
import { ChangeStatusRequest, DeleteData, PaginationParams, Wrapper } from '../../type';
import {
  ChangeStatusClientResponse,
  ClientRequest,
  ClientResponse,
  DeleteClientResponse,
  DriverRequest,
  DriverResponse,
  GetAccountsResponse,
  GetClientByIdResponse,
  GetClientsDropdownResponse,
  GetClientsResponse,
  GetHostingDetailsByIdResponse,
  GetHostingDetailsResponse,
  GetReviewsResponse,
  GetCustomerIntegrationByIdResponse,
  GetCustomerIntegrationsResponse,
  CustomerIntegrations,
  HostingDetails,
} from './interface';

export class ClientProfile extends BaseService {
  async createClient(req: ClientRequest, typeName: string) {
    return this.post<ClientResponse, ClientRequest>(`${this.apiUrl}/api/${typeName}`, req);
  }

  async getClients(req: PaginationParams, typeName: string) {
    const params = new URLSearchParams();

    params.append('size', String(req.size));

    if (req.page !== undefined) params.append('page', String(req.page));

    if (req.name) params.append('name', String(req.name));

    if (req.userName) params.append('userName', String(req.userName));

    if (req.accountTitle) params.append('accountTitle', String(req.accountTitle));

    if (req.contractNumber) params.append('contractNumber', String(req.contractNumber));

    if (req.source) params.append('source', String(req.source));

    if (Array.isArray(req.type)) {
      req.type.forEach((val) => params.append('type', String(val)));
    } else if (req.type !== undefined) {
      params.append('type', String(req.type));
    }

    return this.get<GetClientsResponse>(`${this.apiUrl}/api/${typeName}/paged`, params);
  }

  async getCustomers(params: PaginationParams) {
    return this.get<GetClientsResponse>(`${this.apiUrl}/api/customers/paged`, params);
  }

  async getClientsDropdown(req: PaginationParams) {
    const params = new URLSearchParams();

    params.append('size', String(req.size));

    params.append('search', String(req?.search || ''));

    if (req.page !== undefined) params.append('page', String(req.page));

    if (Array.isArray(req.type)) {
      req.type.forEach((val) => params.append('type', String(val)));
    } else if (req.type !== undefined) {
      params.append('type', String(req.type));
    }

    return this.get<GetClientsDropdownResponse>(`${this.apiUrl}/api/users/client/get`, params);
  }

  async updateClient(id: number, req: ClientRequest, typeName: string) {
    return this.put<ClientResponse, ClientRequest>(`${this.apiUrl}/api/${typeName}/${id}`, req);
  }

  async deleteClient(id: number, typeName: string) {
    return this.delete<DeleteClientResponse>(`${this.apiUrl}/api/${typeName}/${id}`);
  }

  async changeStatusClient(id: number, active: string, req: ChangeStatusRequest) {
    return this.put<ChangeStatusClientResponse, ChangeStatusRequest>(
      `${this.apiUrl}/api/${req.typeName}/${active}/${id}`,
      req
    );
  }

  async getClientByID(id: number, typeName: string) {
    return this.get<GetClientByIdResponse>(`${this.apiUrl}/api/${typeName}/${id}`);
  }

  async getDrivers(req: PaginationParams, accountId: number) {
    const params = new URLSearchParams();

    params.append('size', String(req.size));

    if (req.active) params.append('active', String(req.active));

    if (req.name) params.append('name', String(req.name));

    if (req.page !== undefined) params.append('page', String(req.page));

    if (Array.isArray(req.type)) {
      req.type.forEach((val) => params.append('type', String(val)));
    } else if (req.type !== undefined) {
      params.append('type', String(req.type));
    }

    return this.get<GetClientsResponse>(`${this.apiUrl}/api/users/byaccountid/${accountId}`, params);
  }

  async createDriver(req: DriverRequest) {
    return this.post<DriverResponse, DriverRequest>(`${this.apiUrl}/api/users/driver`, req);
  }

  async getAccounts(params: PaginationParams) {
    return this.get<GetAccountsResponse>(`${this.apiUrl}/api/accounts/get`, params);
  }

  async getHostingDetails(params: PaginationParams) {
    return this.get<GetHostingDetailsResponse>(`${this.apiUrl}/api/accounts/hosting`, params);
  }

  async createHostingDetails(req: HostingDetails) {
    return this.post<GetHostingDetailsByIdResponse, HostingDetails>(`${this.apiUrl}/api/accounts/hosting`, req);
  }

  async updateHostingDetails(id: number, req: HostingDetails) {
    return this.put<GetHostingDetailsByIdResponse, HostingDetails>(`${this.apiUrl}/api/accounts/hosting/${id}`, req);
  }

  async deleteHostingDetails(id: number) {
    return this.delete<DeleteData>(`${this.apiUrl}/api/accounts/hosting/${id}`);
  }

  async getHostingDetailsById(id: number | undefined) {
    return this.get<GetHostingDetailsByIdResponse>(`${this.apiUrl}/api/accounts/hosting/${id}`);
  }

  async getReviews(params: PaginationParams) {
    return this.get<GetReviewsResponse>(`${this.apiUrl}/api/reviews/paged`, params);
  }

  async getCustomerIntegrations(businessId: number) {
    return this.get<GetCustomerIntegrationsResponse>(
      `${this.apiUrl}/api/customerintegrations/business/${businessId}`,
    );
  }

  async createCustomerIntegration(req: CustomerIntegrations) {
    return this.post<GetCustomerIntegrationByIdResponse, CustomerIntegrations>(
      `${this.apiUrl}/api/customerintegrations`,
      req,
    );
  }

  async updateCustomerIntegration(
    id: number | undefined,
    req: CustomerIntegrations,
  ) {
    return this.put<GetCustomerIntegrationByIdResponse, CustomerIntegrations>(
      `${this.apiUrl}/api/customerintegrations/${id}`,
      req,
    );
  }

  async getCustomerIntegrationById(id: number | undefined) {
    return this.get<GetCustomerIntegrationByIdResponse>(
      `${this.apiUrl}/api/customerintegrations/${id}`,
    );
  }

  async deleteCustomerIntegration(id: number) {
    return this.delete<Wrapper>(
      `${this.apiUrl}/api/customerintegrations/${id}`,
    );
  }
}
